import { useEffect, useSyncExternalStore } from "react";
import { API_BASE_URL } from "../../config/env";

// Whether the store is open, switched at /admin/marketplace. The header, the cart count and the
// store pages all read it, so it is one shared value: asked for once per page load with a plain
// fetch (no Authorization header, so no CORS preflight), and the last answer is kept in
// localStorage so the header draws right on the first render. Until anything is known the
// store counts as open, which is how it was before the switch existed.

const STORAGE_KEY = "n450s.storeOpen";

function readRemembered(): boolean | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "1" ? true : stored === "0" ? false : null;
  } catch {
    return null;
  }
}

let storeOpen: boolean = readRemembered() ?? true;
let asked = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Sets the value for this browser at once; the admin page calls it after saving. */
export function setStoreOpen(next: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    // private mode or full storage: it just won't be remembered for the next visit
  }
  if (next === storeOpen) return;
  storeOpen = next;
  listeners.forEach((listener) => listener());
}

function askOnce(): void {
  if (asked) return;
  asked = true;
  fetch(`${API_BASE_URL}/store-switch`)
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      if (body && typeof body.enabled === "boolean") setStoreOpen(body.enabled);
    })
    .catch(() => {
      // offline or backend down: keep what we remembered, and ask again on the next mount
      asked = false;
    });
}

export function useStoreOpen(): boolean {
  useEffect(askOnce, []);
  return useSyncExternalStore(subscribe, () => storeOpen);
}
